import { useEffect, useRef, useCallback } from 'preact/hooks';
import { signalRService } from '@/services/signalRService';
/**
 * Manages SignalR subscription for email asset generation streaming events.
 * Joins the `emailasset-{assetId}` group and listens for `EmailAssetGenerationEvent`.
 *
 * Pattern: useLandingPageSignalR.ts — callbacks stored in refs to prevent
 * mid-generation unsubscribe/resubscribe from callback identity changes.
 */
export function useEmailAssetSignalR({ assetId, onThinking, onContent, onToolUse, onToolResult, onResult, onError, onDone, onAnalyzing, onRebuilding, onRebuilt, onRebuildRejected, onConversationalResponse, onAssistantMessageAppended, onChatStateChanged, onUserMessageAppended, }) {
    const handlerRef = useRef(null);
    const callbacks = {
        onThinking, onContent, onToolUse, onToolResult, onResult, onError, onDone,
        onAnalyzing, onRebuilding, onRebuilt, onRebuildRejected, onConversationalResponse,
        onAssistantMessageAppended, onChatStateChanged, onUserMessageAppended,
    };
    const callbacksRef = useRef(callbacks);
    callbacksRef.current = callbacks;
    const cleanup = useCallback(() => {
        const entry = handlerRef.current;
        if (entry) {
            signalRService.off('EmailAssetGenerationEvent', entry.handler);
            signalRService.leaveGroup(entry.group);
            handlerRef.current = null;
        }
    }, []);
    useEffect(() => {
        if (!assetId)
            return;
        const group = `emailasset-${assetId}`;
        let cancelled = false;
        const handleEvent = (payload) => {
            const raw = payload;
            // Handle both PascalCase (C# default) and camelCase (JS SignalR default)
            const event = {
                Type: (raw.Type ?? raw.type),
                Content: (raw.Content ?? raw.content),
                Tool: (raw.Tool ?? raw.tool),
                InputTokens: (raw.InputTokens ?? raw.inputTokens),
                OutputTokens: (raw.OutputTokens ?? raw.outputTokens),
                ModelId: (raw.ModelId ?? raw.modelId),
                Phase: (raw.Phase ?? raw.phase),
                WillRebuild: (raw.WillRebuild ?? raw.willRebuild),
                AssetId: (raw.AssetId ?? raw.assetId),
                TurnUserId: (raw.TurnUserId ?? raw.turnUserId),
                TurnUserName: (raw.TurnUserName ?? raw.turnUserName),
            };
            // Filter foreign-asset events. The shared SignalR connection is in every
            // group joined by every active hook, so events from sibling assets monitored
            // by useBulkEmailGeneration leak into this handler. Server-side every event
            // now carries AssetId; reject anything that doesn't match. If the field is
            // missing (older API), fall through to per-case content-based filters below.
            if (event.AssetId && event.AssetId !== assetId)
                return;
            const cb = callbacksRef.current;
            switch (event.Type) {
                case 'thinking':
                    cb.onThinking?.(event.Content ?? '');
                    break;
                case 'content':
                    cb.onContent?.(event.Content ?? '');
                    break;
                case 'tool_use':
                    cb.onToolUse?.(event.Tool ?? '');
                    break;
                case 'tool_result':
                    cb.onToolResult?.(event.Tool ?? '');
                    break;
                case 'result':
                    cb.onResult?.(event);
                    break;
                case 'error':
                    cb.onError?.(event.Content ?? 'An error occurred during generation');
                    break;
                case 'done':
                    cb.onDone?.();
                    break;
                case 'analyzing':
                    cb.onAnalyzing?.();
                    break;
                case 'rebuilding':
                    cb.onRebuilding?.();
                    break;
                case 'rebuilt': {
                    // Filter foreign-asset completions. The shared connection
                    // event handler fires for every rebuilt event the client
                    // receives, including events from emailasset-{otherId}
                    // groups joined by useBulkEmailGeneration. Without this
                    // guard, the builder for asset A would trigger its rocket
                    // animation whenever any bulk-batch asset completes.
                    // `rebuilt` payload content is `asset:{assetId}:v{ticks}`.
                    const eventAssetId = (event.Content ?? '').split(':')[1];
                    if (eventAssetId && eventAssetId !== assetId)
                        break;
                    cb.onRebuilt?.(event.Content ?? '');
                    break;
                }
                case 'rebuildRejected':
                    // Server rejected the rebuild (e.g. an edit that would have broken a
                    // protected template slot) and kept the prior draft. Terminal like
                    // 'rebuilt'/'error', but not an error — Content is user-safe text. The
                    // AssetId filter above already scopes this to our asset.
                    cb.onRebuildRejected?.(event.Content ?? '');
                    break;
                case 'conversationalResponse':
                    cb.onConversationalResponse?.(event.Content ?? '', event.WillRebuild === true, event.ModelId ?? null, event.OutputTokens ?? null);
                    break;
                case 'assistantMessageAppended':
                    cb.onAssistantMessageAppended?.(event.Content ?? '', event.ModelId ?? null, event.OutputTokens ?? null);
                    break;
                case 'token_usage':
                    // Sparse end-of-turn snap; consumed server-side by AgentCostRecorder.
                    // Dashboard reads per-message model+tokens from the persisted chat
                    // message via MessageTokenFooter, not from this event.
                    break;
                case 'chatStateChanged':
                    cb.onChatStateChanged?.(event.Content ?? 'Idle', event.TurnUserId ?? null, event.TurnUserName ?? null);
                    break;
                case 'userMessageAppended':
                    cb.onUserMessageAppended?.(event.Content ?? '', event.TurnUserId ?? null, event.TurnUserName ?? null);
                    break;
            }
        };
        signalRService.joinGroup(group).then(() => {
            if (cancelled) {
                signalRService.leaveGroup(group);
                return;
            }
            signalRService.on('EmailAssetGenerationEvent', handleEvent);
            handlerRef.current = { handler: handleEvent, group };
        }).catch((err) => {
            console.error('Failed to join email asset SignalR group:', err);
        });
        return () => {
            cancelled = true;
            cleanup();
        };
    }, [assetId, cleanup]);
    return { cleanup };
}
